import {inject, Injectable} from "@angular/core";
import {ProjectTeamOverviewService} from "../services/project-team-overview.service";
import {BehaviorSubject, EMPTY, expand, filter, finalize, map, Observable, reduce, switchMap, tap, toArray} from "rxjs";
import {ProjectRequestDto} from "../dto/project-request.dto";
import { DataManipulationService } from "src/app/services/data-manipulation.service";
import {ProjectResponseDto} from "../dto/project-response.dto";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class PTOFacade {
    private projectApi = inject(ProjectTeamOverviewService);
    private data = inject(DataManipulationService);
    private translate = inject(TranslateService);

    public intervals: any[] = [];
    public activeTypeSort: any;

    private projectsSubject = new BehaviorSubject<any>([]);
    readonly projects$ = this.projectsSubject.asObservable();
    private loadingSubject = new BehaviorSubject<boolean>(false);
    readonly loading$ = this.loadingSubject.asObservable();
    private loadingProjectsSubject = new BehaviorSubject<boolean>(false);
    readonly loadingProjects$ = this.loadingProjectsSubject.asObservable();
    private aggregateValuesSubject = new BehaviorSubject<any>({});
    readonly aggregateValues$ = this.aggregateValuesSubject.asObservable();
    private loadingAggregateValuesSubject = new BehaviorSubject<boolean>(false);
    readonly loadingAggregateValues$ = this.loadingAggregateValuesSubject.asObservable();
    private projectTypesSubject = new BehaviorSubject<any>([]);
    readonly projectTypes$ = this.projectTypesSubject.asObservable();
    private projectActiveTypeSubject = new BehaviorSubject<any>({});
    readonly projectActiveType$ = this.projectActiveTypeSubject.asObservable();
    private projectDefaultCategoriesSubject = new BehaviorSubject<any>([]);
    readonly projectDefaultCategories$ = this.projectDefaultCategoriesSubject.asObservable();
    private projectsHighlightsSubject = new BehaviorSubject<any>([]);
    readonly projectsHighlights$ = this.projectsHighlightsSubject.asObservable();
    private readonly quickMenuDataSubject = new BehaviorSubject<any | null>(null);
    readonly quickMenuData$ = this.quickMenuDataSubject.asObservable();
    private archivedProjectsSubject = new BehaviorSubject<any>([]);
    readonly archivedProjects$ = this.archivedProjectsSubject.asObservable();
    private archivedProjectsLoaded = false;

    loadData(payload: ProjectRequestDto): void {
      this.loadingSubject.next(true);
      this.projectApi.getIndicatorInterval().pipe(
        switchMap(intervals => this.getProjects(payload, intervals).pipe(
          map(projects => ({
            intervals: [...intervals],
            projects
          }))
        )),
        switchMap(data => this.projectApi.getProjectTeamTypes().pipe(
          map(projectTypes => {
            const projects = projectTypes._embedded.project ?? [];

            return {
              ...data,
              projectTypes: projects.length >= 2
                ? [
                    {
                      name: this.translate.instant('projects_overview_all_projects_types_filter_item'),
                      uuid: null
                    },
                    ...projects
                  ]
                : projects
            };
          })
        )),
        switchMap(data => this.loadAggregateValues(null, data.intervals).pipe(
          map(aggregateValues => ({
            ...data,
            aggregateValues
          }))
        )),
        switchMap(data => this.projectApi.getProjectDefaultCategories().pipe(
          map(projectDefaultCategories => ({
            ...data,
            projectDefaultCategories: [
              {
                title: this.translate.instant('projects_overview_all_indicators_filter_item'),
                uuid: null
              },
              ...(projectDefaultCategories._embedded.categories
                .map((category: any, index: number) => ({
                  ...category,
                  index: index
                })) ?? [])
            ]
          }))
        )),
        switchMap(data => this.projectApi.getProjectsOverviewHighlights().pipe(
          map(projectsHighlights => ({
            ...data,
            projectsHighlights: (projectsHighlights._embedded['workspace-highlights'] ?? []).slice(0,3)
          }))
        )),
        finalize(() => this.loadingSubject.next(false))
      ).subscribe((res: any) => {
        this.projectsSubject.next(res.projects);
        this.aggregateValuesSubject.next(res.aggregateValues);
        this.projectActiveTypeSubject.next(res.projectTypes.length < 2 ? res.projectTypes[0] : null);
        this.projectTypesSubject.next(res.projectTypes);
        this.projectDefaultCategoriesSubject.next(res.projectDefaultCategories);
        this.projectsHighlightsSubject.next(res.projectsHighlights);
        this.intervals = [...res.intervals];
      });
    }

    onFiltersLoadProjects(payload: ProjectRequestDto, intervals: any[]): void {
      this.getProjects(payload, intervals).subscribe((res: any) => {
        this.projectsSubject.next(res);
      });
    }

    loadArchivedProjects(): void {
      if (this.archivedProjectsLoaded) {
        return;
      }

      this.projectApi.getArchivedProjects().pipe(
        map((response: any) => (response?._embedded?.projects ?? [])
          .map((project: any) => this.mapArchivedProjectCard(project)))
      ).subscribe((res: any) => {
        this.archivedProjectsLoaded = true;
        this.archivedProjectsSubject.next(res);
      });
    }

    setProjectFilterBasedOnType(type: any) {
      this.projectActiveTypeSubject.next(type);
    }

    private getProjects(payload: ProjectRequestDto, intervals: any): Observable<any> {
      this.loadingProjectsSubject.next(true);
      return this.projectApi.loadProjects(payload).pipe(
        expand((response: any) => {
          if (response.page < response.pageCount) {
            return this.projectApi.loadProjects({
              ...payload,
              params: {
                ...payload.params,
                page: response.page + 1
              }
            });
          }
          return EMPTY;
        }),
        map((response: any) =>
          response.pojects.map((project: ProjectResponseDto) => {
            const color = project.score ? this.getColorForScore(project.score, intervals) : this.data.colors.grey;
            const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
            return this.addIndicatorsToProjects({
              ...project,
              project: {
                ...project.project,
                color,
                fadeColor: this.getRgba(color, (project.score ? 0.1 : 0.4)),
                colorLoaded: true,
                newProject: (Date.now() - new Date(project.project.startDate.date).getTime()) <= THIRTY_DAYS,
                isPaused: project.project.status === 'paused' || project.project.status === 'inactive'
              }
            }, intervals)
          })
        ),
        toArray(),
        map(pages => pages.flat()),
        finalize(() => this.loadingProjectsSubject.next(false))
      );
    }

    private loadAggregateValues(sortingParam: any, intervals: any[]): Observable<any> {
      this.loadingAggregateValuesSubject.next(true);
      return this.projectApi.getAggregateValues(sortingParam).pipe(
        map((values: any) => {
          const color = this.getColorForScore(values.score, intervals);
          const responseRateColor = this.getColorForScore(values.score, intervals);
          return this.mapAggregatedResponse({
            ...values,
            color,
            responseRateColor: responseRateColor,
            responseRateFadeColor: intervals.find(interval => (interval.min <= values.score && values.score <= interval.max)).indicatorColor.color,
            diff: values.previousScore ? {
              value: values.score - values.previousScore,
              trend: values.score > values.previousScore ? 'up':'down'
            } : null,
          }, intervals)
        }),
        finalize(() => this.loadingAggregateValuesSubject.next(false))
      )
    }

    loadAggregateValuesBasedOnFilter(typeUuid: any, intervals: any[]) {
      this.loadAggregateValues(typeUuid, intervals).subscribe((res: any) => {
        this.aggregateValuesSubject.next(res);
      });
    }

    loadQuickActionsMenuData(projectUuid: string): void {
      this.quickMenuDataSubject.next(null);

      const archivedCard = this.archivedProjectsSubject.value
        .find((card: any) => card.project.uuid === projectUuid);

      if (archivedCard) {
        this.quickMenuDataSubject.next({
          projectDetails: {
            ...archivedCard.project,
            activity: this.getProjectTeamStatus(archivedCard.project)
          },
          members: []
        });
        return;
      }

      this.data.workspaceProjects$.pipe(
        map(woProjects => {
          const project = woProjects.find((project: any) => project.uuid === projectUuid);
          return {
            ...project,
            activity: this.getProjectTeamStatus(project)
          }
        }),
        switchMap(projectDetails => this.projectApi.getProjectTeamMembers(projectUuid).pipe(
          map((members: any) => ({
            projectDetails : projectDetails,
            members: members._embedded.users ?? []
          }))
        ))
      ).subscribe((res: any) => {
        this.quickMenuDataSubject.next(res);
      });
    }

    getProjectTeamStatus(project: any) {
      const today = new Date();
      const projectTeamStatus = project.status;
      const startDate = project.startDate?.date ? new Date(project.startDate.date) : null;
      const endDate = project.endDate?.date ? new Date(project.endDate.date) : null;
      const daysBetween = (from: Date, to: Date): number => Math.max(0, Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)));
      let projectActivity: any = {};

      if (projectTeamStatus === 'archived') {
        projectActivity.color = '#A0ADCE';
        projectActivity.title = this.translate.instant('project_team_dashboard_edit_archived_status');
        projectActivity.remainingDays = null;
        projectActivity.isCompleted = false;
        projectActivity.isOpen = false;
        projectActivity.isPaused = false;
        projectActivity.isNotOpen = false;
        projectActivity.isArchived = true;
        return projectActivity;
      }

      if (endDate && today > endDate) {
        projectActivity.color = '#16C285';
        projectActivity.title = this.translate.instant('project_team_dashboard_completed_status');
        projectActivity.remainingDays = null;
        projectActivity.isCompleted = true;
        projectActivity.isOpen = false;
        projectActivity.isPaused = false;
        projectActivity.isNotOpen = false;
        projectActivity.isArchived = false;
        return projectActivity;
      }

      if (projectTeamStatus === 'active' || projectTeamStatus === 'draft') {
        if (startDate && endDate && today >= startDate && today <= endDate) {
          const remainingDays = daysBetween(today, endDate);
          projectActivity.color = '#16C285';
          projectActivity.title = this.translate.instant('active');
          projectActivity.remainingDays = this.translate.instant(('project_team_dashboard_edit_open_remaining_day'+(remainingDays !== 1 ? 's':'')), {days: remainingDays});
          projectActivity.isCompleted = false;
          projectActivity.isOpen = true;
          projectActivity.isPaused = false;
          projectActivity.isNotOpen = false;
          projectActivity.isArchived = false;
          projectActivity.status = this.translate.instant('project_team_dashboard_edit_open_status');
          return projectActivity;
        }
        if (startDate) {
          const remainingDays = daysBetween(today, startDate);
          projectActivity.color = '#16C285';
          projectActivity.title = this.translate.instant(('project_team_dashboard_edit_next_round_day'+(remainingDays !== 1 ? 's':'')), {days: remainingDays});
          projectActivity.remainingDays = remainingDays;
          projectActivity.isCompleted = false;
          projectActivity.isOpen = false;
          projectActivity.isPaused = false;
          projectActivity.isNotOpen = true;
          projectActivity.isArchived = false;
          return projectActivity;
        }
      }

      if (projectTeamStatus === 'inactive' || projectTeamStatus === 'paused') {
        projectActivity.color = '#A0ADCE';
        projectActivity.title = this.translate.instant('team_feedback_status3');
        projectActivity.remainingDays = null;
        projectActivity.isCompleted = false;
        projectActivity.isOpen = false;
        projectActivity.isPaused = true;
        projectActivity.isNotOpen = false;
        projectActivity.isArchived = false;
        return projectActivity;
      }

      if (projectTeamStatus === 'completed') {
        projectActivity.color = '#16C285';
        projectActivity.title = this.translate.instant('project_team_dashboard_edit_completed_status');
        projectActivity.remainingDays = null;
        projectActivity.isCompleted = true;
        projectActivity.isOpen = false;
        projectActivity.isPaused = false;
        projectActivity.isNotOpen = false;
        projectActivity.isArchived = false;
        return projectActivity;
      }
    }

    pauseProject(project: any) {
      this.projectApi.pauseProjectTeam(project.uuid).subscribe((res: any) => {
        project.status = res.status;
        project.activity = this.getProjectTeamStatus(project);
      });
    }

    resumeProject(project: any) {
      this.projectApi.resumeProjectTeam(project.uuid).subscribe((res: any) => {
        project.status = res.status;
        project.activity = this.getProjectTeamStatus(project);
      });
    }

    private getRgba(hex: string, alpha: number = 1): string {
      hex = hex.replace('#', '');
      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
      if (hex.length !== 6) throw new Error('Hex code invalid');

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    private getColorForScore(score: number, intervals: any[]): string {
      const interval = intervals.find(interval => (interval.min <= score && score <= interval.max));
      //@ts-ignore
      return this.data.colors[interval.indicatorColor.color] ?? this.data.colors.grey;
    }

    private mapArchivedProjectCard(project: any) {
      const color = this.data.colors.grey;

      return {
        project: {
          ...project,
          projectType: project.projectType?.name ?? project.projectType,
          color,
          fadeColor: this.getRgba(color, 0.4),
          colorLoaded: true,
          isArchived: true
        },
        score: null,
        indicators: []
      };
    }

    private addIndicatorsToProjects(project: any, intervals: any[]) {
      const indicators = [
        { title: 'Engagement', score: project.engagementScore ? project.engagementScore : null},
        { title: 'Clarity', score: project.clarityScore ? project.clarityScore : null},
        { title: 'Efficiency', score: project.efficiencyScore ? project.efficiencyScore : null},
        { title: 'Collaboration', score: project.collaborationScore ? project.collaborationScore : null},
        { title: 'Workload', score: project.workloadScore ? project.workloadScore : null},
        { title: 'Quality', score: project.qualityScore ? project.qualityScore : null}
      ].map((indicator: any, index: number) => {
        const color = indicator.score ? this.getColorForScore(indicator.score, intervals) : this.data.colors.grey;

        return {
          ...indicator,
          index: index,
          color,
          fadeColor: this.getRgba(color, (indicator.score ? 0.1 : 0.4))
        };
      });

      const {
        engagementScore,
        clarityScore,
        efficiencyScore,
        collaborationScore,
        workloadScore,
        qualityScore,
        ...rest
      } = project;

      return {
        ...rest,
        indicators
      };
    }

    private mapAggregatedResponse(response: any, intervals: any[]) {
      const indicators = [
        { title: 'Engagement', slug: 'engagement', currentScore: response.engagementScore, previousScore: response.previousEngagementScore },
        { title: 'Clarity', slug: 'clarity', currentScore: response.clarityScore, previousScore: response.previousClarityScore },
        { title: 'Efficiency', slug: 'efficiency', currentScore: response.efficiencyScore, previousScore: response.previousEfficiencyScore },
        { title: 'Collaboration', slug: 'collaboration', currentScore: response.collaborationScore, previousScore: response.previousCollaborationScore },
        { title: 'Workload', slug: 'workload', currentScore: response.workloadScore, previousScore: response.previousWorkloadScore },
        { title: 'Quality', slug: 'quality', currentScore: response.qualityScore, previousScore: response.previousQualityScore }
      ].map(indicator => {
        const color = this.getColorForScore(indicator.currentScore, intervals);
        //@ts-ignore
        const gradientColor = this.data.colors[intervals.find(interval => (interval.min <= indicator.currentScore && indicator.currentScore <= interval.max)).indicatorColor.color + 'Gradient'];
        return {
          ...indicator,
          trend: indicator.previousScore ? (indicator.currentScore > indicator.currentScore ? 'up':'down') : null,
          color,
          gradientColor,
          fadeColor: this.getRgba(color, 0.1)
        };
      });

      const {
        engagementScore,
        previousEngagementScore,
        clarityScore,
        previousClarityScore,
        efficiencyScore,
        previousEfficiencyScore,
        collaborationScore,
        previousCollaborationScore,
        workloadScore,
        previousWorkloadScore,
        qualityScore,
        previousQualityScore,
        ...rest
      } = response;

      return {
        ...rest,
        indicators
      }
    }
}
